import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lightbulb } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { PageHeader } from '../../components/layout/AppShell'
import { Button } from '../../components/ui/button'
import { ErrorNote } from '../../components/ui/callout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Input, Label, Select, Skeleton, Textarea } from '../../components/ui/primitives'
import { FieldError, SchemaFieldInput } from '../../components/ui/schema-form'
import { useToast } from '../../components/ui/toast'
import { ApiError, api } from '../../lib/api'
import type { ArticleSummary, CatalogItem, TicketDetail } from '../../lib/types'
import { cn } from '../../lib/utils'
import {
  buildNewTicketPayload,
  TICKET_CATEGORY_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
  type TicketCategory,
  type TicketPriority,
} from '../ticket-create'

export function NewTicket() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // Arriving from the catalog: /new?item=12 preselects that item, so choosing a
  // service and then re-choosing it from a dropdown is not the first thing the
  // catalog asks of somebody who has already chosen.
  const [params] = useSearchParams()

  const catalog = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.get<CatalogItem[]>('/catalog/'),
  })

  const [subcategoryId, setSubcategoryId] = useState<number | null>(
    () => Number(params.get('item')) || null,
  )
  const [category, setCategory] = useState<TicketCategory>('issue')
  const [priority, setPriority] = useState<TicketPriority>('medium')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})

  const selected = useMemo(
    () => catalog.data?.find((item) => item.id === subcategoryId) ?? null,
    [catalog.data, subcategoryId],
  )
  const schemaFields = selected?.form_schema?.fields ?? []
  const serviceError = fieldErrors.service_subcategory?.[0] ?? (
    subcategoryId !== null && !selected
      ? 'That service is not available. Choose another service.'
      : undefined
  )

  // Reset answers when the catalog item changes — the schema changed with it.
  useEffect(() => {
    setAnswers({})
    setFieldErrors({})
  }, [subcategoryId])

  // SPEC section 8: as the user types the title, query KB search and show
  // matching articles above the submit button. Debounced so it does not fire
  // per keystroke.
  const [debouncedTitle, setDebouncedTitle] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTitle(title), 300)
    return () => clearTimeout(timer)
  }, [title])

  const suggestions = useQuery({
    queryKey: ['kb-search', debouncedTitle],
    queryFn: () => api.get<ArticleSummary[]>(`/kb/?q=${encodeURIComponent(debouncedTitle)}`),
    enabled: debouncedTitle.trim().length >= 3,
  })

  const create = useMutation({
    mutationFn: () =>
      api.postJson<TicketDetail>(
        '/tickets/',
        buildNewTicketPayload({
          category,
          priority,
          serviceSubcategoryId: subcategoryId,
          title,
          description,
          answers,
        }),
      ),
    onSuccess: async (ticket) => {
      await queryClient.invalidateQueries({ queryKey: ['tickets'] })
      toast({ title: `Ticket ${ticket.ref} raised`, variant: 'success' })
      navigate(`/tickets/${ticket.ref}`)
    },
    onError: (error) => {
      if (error instanceof ApiError) setFieldErrors(error.fields)
    },
  })

  if (catalog.isLoading) {
    return (
      <div className="max-w-2xl space-y-3">
        <Skeleton className="h-8 w-52" />
        <Skeleton className="h-72 w-full" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <PageHeader title="Raise a ticket" description="Tell us what you need." />

      {catalog.isError && (
        <div className="mb-4 space-y-2">
          <ErrorNote message="Services could not be loaded. Please try again." />
          <Button variant="outline" onClick={() => catalog.refetch()}>Retry</Button>
        </div>
      )}

      <Card>
        <CardContent className="space-y-4 pt-4">
          <div>
            <Label htmlFor="service">
              Service <span className="text-critical">*</span>
            </Label>
            <Select
              id="service"
              className="mt-1"
              required
              value={selected?.id ?? ''}
              disabled={catalog.isError || !catalog.data?.length}
              aria-invalid={Boolean(serviceError) || undefined}
              aria-describedby={serviceError ? 'service-error' : undefined}
              onChange={(event) => setSubcategoryId(Number(event.target.value) || null)}
            >
              <option value="">Choose a service</option>
              {catalog.data?.map((item) => (
                <option key={item.id} value={item.id}>{item.service} / {item.name}</option>
              ))}
            </Select>
            <FieldError id="service-error" message={serviceError} />
            {!catalog.isError && !catalog.data?.length && (
              <ErrorNote message="No services are available to your account. Contact your administrator." />
            )}
          </div>

          <div>
            <Label htmlFor="category">
              Category <span className="text-critical">*</span>
            </Label>
            <Select
              id="category"
              required
              className="mt-1"
              value={category}
              aria-invalid={Boolean(fieldErrors.category) || undefined}
              aria-describedby={fieldErrors.category ? 'category-error' : undefined}
              onChange={(event) => setCategory(event.target.value as TicketCategory)}
            >
              {TICKET_CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FieldError id="category-error" message={fieldErrors.category?.[0]} />
          </div>

          <div>
            <Label htmlFor="priority">
              Priority <span className="text-critical">*</span>
            </Label>
            <Select
              id="priority"
              required
              className="mt-1"
              value={priority}
              aria-invalid={Boolean(fieldErrors.priority) || undefined}
              aria-describedby={fieldErrors.priority ? 'priority-error' : undefined}
              onChange={(event) => setPriority(event.target.value as TicketPriority)}
            >
              {TICKET_PRIORITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FieldError id="priority-error" message={fieldErrors.priority?.[0]} />
          </div>

          <div>
            <Label htmlFor="title">
              Subject <span className="text-critical">*</span>
            </Label>
            <Input
              id="title"
              className={cn('mt-1', fieldErrors.title && 'border-critical')}
              value={title}
              aria-invalid={Boolean(fieldErrors.title) || undefined}
              aria-describedby={fieldErrors.title ? 'title-error' : undefined}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Laptop will not power on"
            />
            <FieldError id="title-error" message={fieldErrors.title?.[0]} />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={4}
              className="mt-1"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {schemaFields.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle>{selected?.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {schemaFields.map((field) => (
              <SchemaFieldInput
                key={field.name}
                field={field}
                value={answers[field.name] ?? ''}
                error={fieldErrors[field.name]?.[0]}
                onChange={(value) => setAnswers((prev) => ({ ...prev, [field.name]: value }))}
              />
            ))}
            {fieldErrors.form_data && <ErrorNote message={fieldErrors.form_data[0]} />}
          </CardContent>
        </Card>
      )}

      {(suggestions.data?.length ?? 0) > 0 && (
        <Card className="mt-4 border-info/30 bg-info/5">
          <CardContent className="pt-4">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-info">
              <Lightbulb className="size-4" aria-hidden />
              These articles might solve it without a ticket
            </h2>
            <ul className="mt-2 space-y-1">
              {suggestions.data?.map((article) => (
                <li key={article.slug}>
                  <Link
                    to={`/kb/${article.slug}`}
                    className="text-sm text-primary underline underline-offset-2"
                  >
                    {article.title}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {create.error && (
        <div className="mt-4 space-y-2">
          <ErrorNote message={(create.error as Error).message} />
          {fieldErrors.hint?.[0] && <p className="text-sm">{fieldErrors.hint[0]}</p>}
          {fieldErrors.ref?.[0] && (
            <Link className="text-sm text-primary underline" to={`/tickets/${fieldErrors.ref[0]}`}>
              View saved ticket {fieldErrors.ref[0]}
            </Link>
          )}
        </div>
      )}

      <Button
        size="lg"
        className="mt-4"
        onClick={() => create.mutate()}
        disabled={create.isPending || !selected || catalog.isError || !title.trim()}
      >
        {create.isPending ? 'Submitting…' : 'Submit ticket'}
      </Button>
    </div>
  )
}
